import { Common } from '@thecvlb/design-system';
import { ZodFieldError } from 'pages/MedicationTemplates/medicationTemplates.validation';
import { toast } from 'react-toastify';

export const notifyError = (message: string) =>
  toast.error(message, {
    className: 'justify-start',
    closeOnClick: true,
    icon: <Common.Icon name="error" className="size-9 text-red" />,
    toastId: message
  });

const MAX_VISIBLE_ERRORS = 4;

/**
 * Shows a structured validation error toast listing each invalid field by name.
 * Use when server-side Zod validation fails — pass the output of `parseZodMessage`.
 * `autoClose: false` keeps the toast until dismissed, since the list may be long.
 * At most MAX_VISIBLE_ERRORS entries are shown; a "+N more" line appears when truncated.
 */

export const notifyValidationError = (fields: ZodFieldError[]) => {
  const visible = fields.slice(0, MAX_VISIBLE_ERRORS);
  const overflow = fields.length - MAX_VISIBLE_ERRORS;

  return toast.error(
    <div>
      <p className="mb-1 font-semibold">Please fix the following fields:</p>
      <ul className="ml-1 list-disc pl-3 text-sm">
        {visible.map(({ field, reason }, index) => (
          <li key={`${field}-${index}`}>
            <span className="font-medium">{field}</span>
            {reason ? <span className="text-red/60"> — {reason}</span> : null}
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className="mt-1 text-xs text-red/60">
          +{overflow} more error{overflow > 1 ? 's' : ''} — scroll up to review
        </p>
      )}
    </div>,
    {
      autoClose: false,
      className: 'justify-start',
      closeOnClick: false,
      icon: <Common.Icon className="size-9 text-red" name="error" />,
      toastId: 'validation-error'
    }
  );
};

export const notifySuccess = (message: string) =>
  toast.success(message, {
    className: 'justify-start',
    closeOnClick: true,
    icon: <Common.Icon name="check" color="text-secondary" />,
    toastId: message
  });
